<template>
  <WithLoader :isLoading="isLoading" classType="primary">
    <div v-if="activities.length" class="contacts-activities-table__wrapper">
      <VDataTable
        extraClass="contacts-activities-table"
        classType="primary"
        hidePlaceholder
      >
        <template slot="thead">
          <tr>
            <th
              v-for="(header, idx) in tableFields.headers"
              :key="idx"
              :class="{
                [header.extraClass]: !!header.extraClass
              }"
            >
              <span> {{ header.label }} </span>
            </th>
          </tr>
        </template>

        <template slot="tbody">
          <template v-for="row in tableFields.rows">
            <tr
              :key="row.key"
              :class="{
                [row.extraClass]: !!row.extraClass
              }"
            >
              <td
                v-for="v in row.values"
                :key="v.key"
                :class="{ [v.extraClass]: !!v.extraClass, ['link']: !!v.link }"
              >
                <component
                  v-if="v.component && !v.hidden"
                  :is="v.component"
                  v-bind="v.props"
                ></component>
                <div class="tr-value" v-else-if="!v.hidden">
                  <span> {{ v.value }} </span>
                </div>
              </td>
            </tr>

            <tr v-if="expandedId === row.id">
              <td class="expanded-row" colspan="7">
                <ContactsActivityTimeline
                  :activityItems="_getActivityTrackingItems(row.activity)"
                />
              </td>
            </tr>
          </template>
        </template>
      </VDataTable>
    </div>
    <div class="no-templates" v-else>
      <span>
        No activities yet!
      </span>
    </div>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import moment from "moment";
import { mapGetters, mapActions } from "vuex";

import withCopy from "@/mixins/withCopy";
import withToasted from "@/mixins/withToasted";

// components
import VLink from "@/components/VLink";
import VTooltip from "@/components/VTooltip";
import VPreviewImage from "@/components/VPreviewImage";
import VDateTooltiped from "@/components/VDateTooltiped";

import mailingSvg from "@/assets/images/circuits-pink.svg";

import {
  getFormattedDate,
  getPostcardFullType,
  processMailpieceSource
} from "@/utils/util";

import {
  ActivityTrackingLabelsMap,
  ActivityTrackingOrder
} from "@/constants/common";

import { getTableContextMenu } from "@/utils/table";
import { fetchMailPieceThumbnail } from "@/api-client/contacts";

const StatusWithTime = Vue.component("status-time", {
  props: {
    id: VueTypes.string,
    name: VueTypes.string,
    edited: VueTypes.string,
    onClick: VueTypes.func,
    expanded: VueTypes.bool.def(false)
  },

  components: {
    VTooltip
  },

  template: `<VTooltip position="up" :text="edited" extraClass="status-time">
                <div class="status-value" @click="e => onClick && onClick(e, id)">
                  <span> {{ name }} </span>
                  <i class="caret" :class="getCaretIcon()"></i>
                </div>
            </VTooltip>`,

  methods: {
    getCaretIcon() {
      return `fa fa-caret-${this.expanded ? "up" : "down"}`;
    }
  }
});

const CampaignSourceTitle = Vue.component("ContactsDetailsActivityTable", {
  props: ["name", "id"],
  components: {
    VTooltip
  },

  data() {
    return {
      ActivityTrackingLabelsMap
    };
  },

  template: `<VTooltip
              :text="name"
              position="up"
              extraClass="source-title"
            >
              <VLink
                v-if="!_campaignDeleted"
                label="Campaign"
                :routerLink="getLink()"
                extraClass="contact-activity-link"
              ></VLink>
              <span v-else> Campaign </span>
            </VTooltip>`,

  methods: {
    getLink() {
      return `/campaigns/${this.id}`;
    }
  },

  computed: {
    _campaignDeleted() {
      return this._getCampaign && !!this._getCampaign.deleted_at;
    },

    ...mapGetters("campaigns", ["getCampaign"])
  }
});

export default Vue.component("ContactsDetailsActivityTable", {
  props: {
    id: VueTypes.string,
    activities: VueTypes.any,
    activityTrackingItems: VueTypes.object,
    expandedId: VueTypes.string,
    type: VueTypes.oneOf(["letters", "postcards"]).def("postcards")
  },

  components: {
    VLink,
    VTooltip
  },

  mixins: [withCopy, withToasted],

  data() {
    return {
      opened: [],
      mailingSvg,
      downloadingPdfId: null
    };
  },

  computed: {
    tableFields() {
      if (this.type === "letters") {
        return {
          headers: [
            {
              key: "preview",
              label: ""
            },
            {
              key: "color",
              label: "Colored",
              extraClass: "color"
            },
            {
              key: "double_sided",
              label: "Double Sided"
            },
            {
              key: "source",
              label: "Source"
            },
            {
              key: "created_at",
              label: "Created"
            },
            {
              key: "status",
              label: "Status"
            },
            {
              key: "v-context-menu",
              label: "",
              extraClass: "contact-activity-actions"
            }
          ],

          rows: this.activities.map(mailpiece => ({
            id: mailpiece.id,
            activity: mailpiece,
            values: [
              {
                key: "preview",
                component: VPreviewImage,
                props: {
                  image: mailpiece.thumbnail_url,
                  withPdfDownload: !!mailpiece.url_to_file,
                  hideTitle: true,
                  pdfDownloadHandler: e => this._downloadPdf(e, mailpiece),
                  pdfLoading: this.downloadingPdfId === mailpiece.id,
                  orientation: "vertical",
                  extraClass: "contacts-details-preview-extra"
                },
                extraClass: `history-preview ${
                  this.type === "letters" ? "letters-preview" : ""
                }`
              },
              {
                key: "colored",
                value: !!mailpiece.color ? "Yes" : "No",
                extraClass: "color"
              },
              {
                key: "double_sided",
                value: !!mailpiece.doubleSided ? "Yes" : "No"
              },
              {
                key: "source",
                value: mailpiece.source
                  ? processMailpieceSource(mailpiece.source)
                  : "Webhook"
              },
              {
                key: "created_at",
                component: VDateTooltiped,
                props: {
                  date: mailpiece.created_at
                }
              },
              {
                key: "status",
                component: StatusWithTime,
                props: {
                  name: ActivityTrackingLabelsMap[mailpiece.latestStatus],
                  expanded: this.expandedId === mailpiece.id,
                  edited: this._getFormattedDate(
                    mailpiece[mailpiece.latestStatus]
                  ),

                  id: mailpiece.id,
                  onClick: (e, id) => this.$emit("expand", id)
                }
              },
              getTableContextMenu(
                {
                  copy: () => this.copy(mailpiece.id)
                  // resend: () => this._resendMailpiece(mailpiece.id)
                },
                { extraClass: "contact-activity-link-menu" }
              )
            ]
          }))
        };
      }

      return {
        headers: [
          {
            key: "preview",
            label: ""
          },
          {
            key: "name",
            label: "Template Name",
            extraClass: "name"
          },
          {
            key: "template_type",
            label: "Type"
          },
          {
            key: "created_at",
            label: "Created"
          },
          {
            key: "source",
            label: "Source",
            link: true
          },
          {
            key: "status",
            label: "Status"
          },
          {
            key: "v-context-menu",
            label: "",
            extraClass: "contact-activity-actions"
          }
        ],

        rows: this.activities.map(mailpiece => ({
          id: mailpiece.id,
          activity: mailpiece,
          values: [
            {
              key: "preview",
              component: VPreviewImage,
              props: {
                image: mailpiece.front_thumbnail_url,
                withPdfDownload: !!mailpiece.pdf_url,
                hideTitle: true,
                pdfDownloadHandler: e => this._downloadPdf(e, mailpiece),
                pdfLoading: this.downloadingPdfId === mailpiece.id,
                extraClass: "contacts-details-preview-extra"
              },
              extraClass: "history-preview"
            },
            {
              key: "name",
              component: VLink,
              props: {
                routerLink: `/postcards-templates/settings/${mailpiece.template.id}&postcard`,
                label: mailpiece.template.name,
                extraClass: "history-name-link"
              },
              extraClass: "name",
              link: true
            },
            {
              key: "template_type",
              value: getPostcardFullType(
                mailpiece.template.type,
                mailpiece.template.mail_type
              )
            },
            {
              key: "created_at",
              component: VDateTooltiped,
              props: {
                date: mailpiece.created_at
              }
            },
            {
              key: "source",
              value: processMailpieceSource(mailpiece.source),
              component:
                mailpiece.source === "campaign" ? CampaignSourceTitle : null,
              props: {
                name: mailpiece.campaign_name,
                id: mailpiece.campaign_id
              }
            },
            {
              key: "status",
              component: StatusWithTime,
              props: {
                name: ActivityTrackingLabelsMap[mailpiece.latestStatus],
                expanded: this.expandedId === mailpiece.id,
                edited: this._getFormattedDate(
                  mailpiece[mailpiece.latestStatus]
                ),

                id: mailpiece.id,
                onClick: (e, id) => this.$emit("expand", id)
              }
            },
            getTableContextMenu(
              {
                copy: () => this.copy(mailpiece.id),
                resend: () => this._resendMailpiece(mailpiece.id)
              },
              { extraClass: "contact-activity-link-menu" }
            )
          ]
        }))
      };
    },

    ...mapGetters("history", ["isLoading"])
  },

  methods: {
    async _downloadPdf(e, activity) {
      e.stopPropagation();

      if (this.type === "postcards") {
        e.stopPropagation();

        if (activity.pdf_url) {
          window.open(activity.pdf_url, "_blank");
        }
      } else {
        e.stopPropagation();

        if (activity.url_to_file) {
          window.open(activity.url_to_file, "_blank");
        }

        return;
      }
    },

    showFile(blob) {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      const newBlob = new Blob([blob], { type: "application/pdf" });

      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob);
        return;
      }

      const data = window.URL.createObjectURL(newBlob);
      const newWindow = window.open("", "_blank");
      newWindow.location = data;
    },

    toggle(id) {
      const index = this.opened.indexOf(id);
      if (index > -1) {
        this.opened.splice(index, 1);
      } else {
        this.opened.push(id);
      }
    },

    _getActivityTrackingItems(activity) {
      const obj = {
        latestStatus: activity.latestStatus
      };

      for (let key of ActivityTrackingOrder) {
        obj[key] = activity[key];
      }

      return obj;
    },

    _getFormattedDate(date) {
      return moment(date).format("MMM DD, YYYY [at] HH:mm");
    },

    _resendMailpiece(id) {
      this.resendMailpiece({
        id,
        onSuccess: () => this.toast("Mailpiece has been resent successfully!"),
        onError: () => this.toast("Failed to resend mailpiece", "error")
      });
    },

    ...mapActions("contacts", ["resendMailpiece"])
  }
});
</script>

<style lang="scss">
$ns: "contacts-activities-table";

.contacts-activities-table__wrapper {
  overflow: auto;
  min-height: 180px;

  .#{$ns} {
    thead,
    tbody {
      th,
      td {
        &.name {
          @include textCropEllipsis();
          padding-left: 20px;
        }
      }
    }

    thead tr th:first-child {
      width: 50px;
    }

    tbody {
      td span,
      td span a.primary {
        color: $secondary-text-color;
      }
    }

    .status-time,
    .campaign-time {
      &.v-tooltip__wrapper {
        [tooltip][flow^="up"]::after {
          @include textCropEllipsis();
        }
      }
    }

    .status-time-title {
      &:hover {
        color: $primary-color;
      }
    }

    .link {
      cursor: pointer;
    }

    .history-activity-link.primary,
    .history-name-vlink.primary {
      font-size: 14px;
      color: $secondary-text-color;

      &:hover {
        color: $primary-color;
      }
    }
  }

  .#{$ns} {
    th,
    td {
      height: 56px;

      &.name {
        padding-left: 10px;
        width: 16%;
        max-width: 16%;

        a {
          vertical-align: middle;
        }
      }

      &.color {
        padding-left: 20px;
      }

      &.country {
        text-align: center;
        width: 10%;
        max-width: 10%;
      }

      &.city-state {
        width: 13%;
        max-width: 13%;
      }

      &.tags {
        padding: 5px 10px;
        width: 18%;
        max-width: 18%;

        &.expanded {
          vertical-align: top;
          padding-top: 10px;
        }
      }

      &.address {
        width: 20%;
        max-width: 20%;
      }

      &.updated-at {
        width: 14%;
        max-width: 14%;
      }

      &.name-preview {
        padding-left: 10px;
        width: 1%;
        padding-right: 0;
      }

      &.history-preview {
        &.letters-preview {
          width: 40px;
          height: 50px;
        }

        &,
        .image-wrapper {
          width: 60px;
          height: 40px;
        }

        &.vertical {
          .image-wrapper {
            width: 40px;
            height: 55px;
            max-width: 40px;
            min-height: 55px;
          }
        }
      }

      &:last-child {
        /*padding: 0;*/

        &.contacts-vlink-menu {
          width: 2%;
          padding-right: 20px;
        }
      }
    }

    th {
      height: 30px;

      &.tags {
        span {
          padding-left: 10px;
        }
      }
    }

    tr {
      &:hover {
        background-color: transparent;

        /*
        td {
          border-top-color: $primary-color;
          border-bottom-color: $primary-color;
        }
        */
      }

      /*
      &:first-child {
        &:hover {
          td {
            border-bottom: 1px solid $primary-color;
          }
        }
      }
      */

      td {
        height: 50px;

        &.name {
          white-space: normal;

          span {
            width: 170px;
            max-width: 170px;
          }
        }

        &.clamped {
          span {
            line-height: 17px;
            @include clamp(34px);
          }
        }

        &.city-state {
          padding-right: 35px;
        }
      }

      &.tags-expand {
        td {
          padding-top: 25px;
          vertical-align: top;

          &.tags {
            padding-top: 5px;
          }

          &.name-preview {
            padding-top: 15px;
          }
        }
      }
    }
  }
}

table tbody td {
  &.contact-activity-link-menu {
    display: flex;
    align-items: center;
    justify-content: center;

    li:hover {
      span {
        color: $primary-color;
      }
    }
  }
}

table.contacts-activities-table {
  tr {
    @include quantity-query(1, less) {
      td.contact-activity-link-menu {
        .menu-wrapper {
          bottom: 35px;
          top: auto;
        }
      }
    }
  }

  td.expanded-row {
    background: transparent;
    min-height: 143px;
    border-top: none;
    padding: 15px 0;
  }
}

table td .status-value {
  cursor: pointer;

  .caret {
    margin-left: 5px;
    color: #d8d8d8;
  }

  &:hover {
    span,
    .caret {
      color: $primary-color;
    }
  }
}

.source-title {
  span a.contact-activity-link {
    font-size: 15px;
  }

  &.v-tooltip__wrapper [tooltip][flow^="up"] {
    width: fit-content;
  }
}

.no-templates {
  span {
    font-size: 16px;
  }
}

.contacts-details-preview-extra.v-preview-image {
  .title {
    .ball {
      transform: translate(0, -18%);
    }

    &.with-download img {
      bottom: 3px;
      right: 3px;
    }
  }

  &.vertical .title {
    .ball {
      transform: translate(0, -10%);
    }
  }
}
</style>
